// src/services/pb.js — the single PocketBase client (frontend.md §5.3 step 1, §8.1).
//
// The hybrid local-first split (AGENTS.md): PocketBase holds shared tenant
// infrastructure only (the 14 collections in schemas/pocketbase.schema.json +
// the `users` auth collection). Confidential content stays in Dexie and NEVER
// routes through this client.
//
// The browser reaches PB through the node server's /pb reverse proxy
// (server/index.js pbProxy) — PB itself is loopback-only. VITE_PB_URL is baked
// at build time; fall back to the same-origin /pb path when unset so dev and
// the container build both resolve without extra config.
import PocketBase from 'pocketbase';

const BASE = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PB_URL) || process?.env?.VITE_PB_URL || '/pb';

export const pb = new PocketBase(BASE);

// pb.authStore persists the token in localStorage automatically. Keep PocketBase
// auto-cancellation from aborting in-flight auth calls on route changes.
pb.autoCancellation(false);

/** True when the SDK holds a valid, unexpired auth token. */
export function isAuthenticated() {
  return Boolean(pb.authStore.isValid && pb.authStore.token);
}

/**
 * Validate + refresh the persisted token on app load (frontend.md §8.4).
 * Returns true when a valid session survives; clears the store on failure.
 */
export async function authRefresh() {
  if (!pb.authStore.token) return false;
  try {
    await pb.collection('users').authRefresh();
    return isAuthenticated();
  } catch {
    pb.authStore.clear();
    return false;
  }
}

/**
 * Request an OTP code for an email. Enumeration-safe: PB returns an otpId even
 * for unknown emails — the UI shows the same "check your inbox" state either way.
 */
export async function requestOtp(email) {
  const { otpId } = await pb.collection('users').requestOTP(email.trim().toLowerCase());
  return otpId;
}

/** Exchange an OTP code for a session token (frontend.md §8.2). */
export async function authWithOtp(otpId, code) {
  await pb.collection('users').authWithOTP(otpId, String(code).trim());
  return pbSessionUser();
}

/** Logout — tokens are stateless; nothing to revoke server-side (§8.2). */
export function signOut() {
  pb.authStore.clear();
}

/** Current PB user as the dataClient session shape (frontend.md §8.4). */
export function pbSessionUser() {
  if (!isAuthenticated()) return null;
  const record = pb.authStore.record;
  return {
    id: record?.id,
    email: record?.email,
    name: record?.name || record?.email,
    role: record?.role || 'owner'
  };
}
